import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Pick pipeline stages by name.
 *
 * Stage names repeat across Rare's pipelines — Closed Lost exists in four — so
 * selecting a NAME selects every stage carrying it. Picking one pipeline's
 * Closed Lost and silently dropping the other three is the failure this avoids,
 * and the count of pipelines using a name is shown so the choice is informed.
 */
export default function StageMultiSelect({
  options = [],
  value = [],
  onChange,
  placeholder = "Choose stages…",
  testId = "stage-select",
}) {
  const [open, setOpen] = useState(false);

  // Collapse ids to distinct labels, counting how many stages share each.
  const names = useMemo(() => {
    const counts = new Map();
    for (const option of options) {
      const label = option.label || option.value;
      counts.set(label, (counts.get(label) || 0) + 1);
    }
    return [...counts.entries()]
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [options]);

  const selected = value || [];

  const toggle = (label) =>
    onChange(
      selected.includes(label) ? selected.filter((v) => v !== label) : [...selected, label],
    );

  return (
    <div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={`flex w-full items-center justify-between gap-2 rounded border border-[#cccccc] bg-white px-2.5 py-1.5 text-left text-sm transition-colors hover:border-[#7d8b96] focus:border-[#57c1a9] focus:outline-none ${
              selected.length ? "text-[#323c44]" : "text-[#7d8b96]"
            }`}
            data-testid={`${testId}-trigger`}
          >
            <span className="truncate">
              {selected.length === 0
                ? placeholder
                : selected.length === 1
                  ? selected[0]
                  : `${selected.length} stages`}
            </span>
            <ChevronsUpDown className="h-3.5 w-3.5 flex-shrink-0 text-[#a9b3bb]" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[340px] p-0">
          <Command>
            <CommandInput placeholder="Search stages…" className="text-sm" />
            <CommandList className="max-h-[300px]">
              <CommandEmpty className="py-6 text-center text-sm text-[#7d8b96]">
                No stage matches that.
              </CommandEmpty>
              <CommandGroup>
                {names.map((entry) => (
                  <CommandItem
                    key={entry.label}
                    value={entry.label}
                    onSelect={() => toggle(entry.label)}
                    className="flex items-center gap-2"
                  >
                    <Check
                      className={`h-3.5 w-3.5 flex-shrink-0 ${
                        selected.includes(entry.label) ? "text-[#119996]" : "text-transparent"
                      }`}
                    />
                    <span className="flex-1 truncate text-sm text-[#323c44]">{entry.label}</span>
                    {entry.count > 1 && (
                      <span className="text-[10px] text-[#a9b3bb]">{entry.count} pipelines</span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {selected.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {selected.map((label) => (
            <span
              key={label}
              className="inline-flex items-center gap-1 rounded bg-[#efefef] px-2 py-0.5 text-[11px] text-[#4a5b68]"
            >
              {label}
              <button
                type="button"
                onClick={() => toggle(label)}
                className="text-[#a9b3bb] hover:text-red-600"
                aria-label={`Remove ${label}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
